import api, { getBaseUrl } from './api';

export const generateInvoices = (year: number, month: number) => api.post(`/invoices/admin/generate/${year}/${month}`);
export const fetchInvoicesForMonth = (year: number, month: number) => api.get(`/invoices/admin/${year}/${month}`);
export const fetchAllInvoices = () => api.get('/invoices/admin');
export const markInvoicePaid = (invoiceId: string, paymentReference?: string) =>
  api.put(`/invoices/admin/${invoiceId}/paid`, paymentReference != null ? { paymentReference } : {});

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
export const updateInvoiceStatus = (invoiceId: string, status: InvoiceStatus, paymentReference?: string) =>
  api.put(`/invoices/admin/invoice/${invoiceId}/status`, { status, ...(paymentReference != null ? { paymentReference } : {}) });

export const uploadMt940 = async (file: File) => {
  const form = new FormData();
  form.append('file', file);
  // Use native fetch so Content-Type is never overridden (axios default json breaks FormData)
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token');
  const res = await fetch(`${baseUrl}/invoices/admin/mt940/upload`, {
    method: 'POST',
    body: form,
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data?.message || 'Upload failed');
    (err as any).response = { data, status: res.status };
    throw err;
  }
  return { data };
};
export const matchMt940 = (transactions: any[]) =>
  api.post('/invoices/admin/mt940/match', { transactions });

export const getSepaSummary = (year: number, month: number, payoutIds?: string[]) =>
  api.get('/invoices/admin/sepa/summary', {
    params: payoutIds?.length ? { payoutIds } : { year, month },
  });

export const getSepaPayouts = (year: number, month: number) =>
  api.get('/invoices/admin/sepa/payouts', { params: { year, month } });

/** Download SEPA XML (uses api so auth is sent); returns blob for download */
export const downloadSepaExport = async (year: number, month: number, payoutIds?: string[]) => {
  const params = payoutIds?.length ? { payoutIds } : { year, month };
  const res = await api.get('/invoices/admin/sepa/export', {
    params,
    responseType: 'blob',
  });
  return res.data as Blob;
};

/** Download admin invoice PDF (uses api so auth is sent); opens in new tab */
export const downloadAdminInvoicePdf = async (invoiceId: string): Promise<void> => {
  const res = await api.get(`/invoices/admin/invoice/${invoiceId}/pdf`, {
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000); // Clean up after 1 min
};

export const fetchProfessionalInvoices = () => api.get('/invoices/professional');
export const fetchProfessionalInvoice = (invoiceId: string) => api.get(`/invoices/professional/${invoiceId}`);
export const createProfessionalInvoice = (data: {
  employer_id: string;
  hours_registration_ids: string[];
  period_start: string;
  period_end: string;
}) => api.post('/invoices/professional/create', data);
export const confirmProfessionalInvoice = (invoiceId: string) =>
  api.post(`/invoices/professional/${invoiceId}/confirm`);

export const fetchEmployerInvoices = () => api.get('/invoices/employer');

/**
 * Factuurdetail voor de opdrachtgever, inclusief de regels.
 *
 * Bij een poolfactuur (verzamelfactuur per discipline) zitten de regels in
 * `pool_lines`. Bij de vertrouwenspersoon-stroom heeft de backend datum, uren
 * en tarief er al afgehaald — die velden zijn daar simpelweg afwezig.
 */
export const fetchEmployerInvoiceDetail = (invoiceId: string) =>
  api.get(`/invoices/employer/${invoiceId}`);

export type InvoiceStream = 'TRAJECT_BATCH' | 'ANONYMOUS_VP' | 'PROJECT_MILESTONE' | 'ACADEMY' | 'B2B' | 'CRISIS' | 'ADHOC';

export interface PoolFactuurRegel {
  id: string;
  line_type: 'SESSIE' | 'REISTOESLAG' | 'MILESTONE' | 'NACALCULATIE' | 'ACADEMY' | 'B2B' | 'UITRUK' | 'REISKOSTEN' | 'ADHOC_MEDEWERKER' | 'ADHOC_INTERN';
  referentie_code: string;
  omschrijving: string;
  kvk_handelsnaam: string;
  kvk_nummer: string | null;
  regel_bedrag: string;
  /** Afwezig bij ANONYMOUS_VP — herleidbaar tot een persoon */
  prestatie_datum?: string | null;
  uren?: string | null;
  uurtarief?: string | null;
  reistijd_zone?: number | null;
  /** Compliance Vetting Tiers (migratie 175): alleen op TRAJECT_BATCH-regels */
  compliance_tier?: 'GOUD' | 'ZILVER' | 'BRONS' | 'ONGEVERIFIEERD' | 'NON_COMPLIANT_HOLD' | null;
  fiscale_categorie?: 'ARBO_VRIJGESTELD' | 'WKR_VRIJE_RUIMTE' | null;
}

export default api;
